import { useMemo } from "react";

import {
  ArchiveBoxIcon,
  ClipboardDocumentCheckIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";

import Avatar from "../user/Avatar";
import LoadingTable from "../shared/LoadingTable";
import Tooltip from "../shared/Tooltip";
import classNames from "../../utils/classNames";
import stringToColour from "../../utils/stringToColour";
import useWilmaUsers from "../../data/useWilmaUsers";
import { IUserPartDataStats } from "./EbayOrdersStats";

const initialsFor = (name: string): string =>
  name
    .split(" ")
    .filter((word) => RegExp(/^\p{L}/, "u").test(word))
    .map((word, i) => (i < 2 ? word.substring(0, 1).toUpperCase() : ""))
    .join("") || "?";

const formatCount = (n: number): string => n.toLocaleString("en-GB");
const formatAvg = (n: number): string =>
  (Math.round(n * 10) / 10).toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

type ColumnKey = "dispatched" | "picked" | "packed";

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  header: string;
  valueCell: string;
  avgCell: string;
  accent: string;
}

const COLUMNS: ColumnConfig[] = [
  {
    key: "dispatched",
    label: "Dispatched",
    icon: PaperAirplaneIcon,
    header:
      "bg-emerald-100/70 text-emerald-800 border-l border-emerald-200",
    valueCell: "bg-emerald-50/50 text-emerald-900",
    avgCell: "bg-emerald-50/50 text-emerald-700",
    accent: "text-emerald-600",
  },
  {
    key: "picked",
    label: "Picked",
    icon: ClipboardDocumentCheckIcon,
    header: "bg-cyan-100/70 text-cyan-800 border-l border-cyan-200",
    valueCell: "bg-cyan-50/50 text-cyan-900",
    avgCell: "bg-cyan-50/50 text-cyan-700",
    accent: "text-cyan-600",
  },
  {
    key: "packed",
    label: "Packed",
    icon: ArchiveBoxIcon,
    header:
      "bg-fuchsia-100/70 text-fuchsia-800 border-l border-fuchsia-200",
    valueCell: "bg-fuchsia-50/50 text-fuchsia-900",
    avgCell: "bg-fuchsia-50/50 text-fuchsia-700",
    accent: "text-fuchsia-600",
  },
];

const countFor = (row: IUserPartDataStats, col: ColumnKey): number => {
  if (col === "dispatched") return row.dispatched;
  if (col === "picked") return row.picked;
  return row.packed;
};

const avgFor = (row: IUserPartDataStats, col: ColumnKey): number => {
  if (col === "dispatched") return row.dailyAvgDispatched;
  if (col === "picked") return row.dailyAvgPicked;
  return row.dailyAvgPacked;
};

export default function OrderStatsTable({
  orderStats,
  isLoading,
}: {
  orderStats: IUserPartDataStats[];
  isLoading?: boolean;
}) {
  const { wilmaUsers } = useWilmaUsers(true);

  const rows = useMemo(() => {
    if (!orderStats) return [];
    // Highest total throughput floats to the top so the top performers
    // are the first thing you see.
    return [...orderStats].sort((a, b) => {
      const total = (r: IUserPartDataStats) =>
        r.dispatched + r.picked + r.packed;
      return total(b) - total(a);
    });
  }, [orderStats]);

  // Compute per-column leader counts so we can highlight the top row.
  const leaders = useMemo(() => {
    const out: Record<ColumnKey, number> = {
      dispatched: 0,
      picked: 0,
      packed: 0,
    };
    for (const row of rows) {
      for (const col of COLUMNS) {
        const n = countFor(row, col.key);
        if (n > out[col.key]) out[col.key] = n;
      }
    }
    return out;
  }, [rows]);

  const resolvesFromDirectory = (row: IUserPartDataStats): boolean =>
    !!wilmaUsers.data?.data?.find((u) => u.id === row.userId);

  if (isLoading && rows.length === 0) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <header className="mb-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Team performance
          </h3>
          <p className="text-[11px] text-slate-500">
            Totals and daily averages per user
          </p>
        </header>
        <LoadingTable
          headers={[
            "User",
            "Dispatched",
            "Avg",
            "Picked",
            "Avg",
            "Packed",
            "Avg",
          ]}
          rows={4}
        />
      </section>
    );
  }

  return (
    <section className="flex h-full flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Team performance
          </h3>
          <p className="text-[11px] text-slate-500">
            Totals and daily averages per user
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
          {rows.length.toLocaleString("en-GB")}{" "}
          {rows.length === 1 ? "user" : "users"}
        </span>
      </header>

      <div className="max-h-[36vh] overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50/95 text-[10px] uppercase tracking-wide text-slate-600 backdrop-blur">
            <tr>
              <th className="w-[38%] px-3 py-2 text-left font-semibold">
                User
              </th>
              {COLUMNS.map((col) => {
                const Icon = col.icon;
                return [
                  <th
                    key={`${col.key}-count`}
                    scope="col"
                    className={classNames(
                      "px-3 py-2 text-right font-semibold",
                      col.header
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      <Icon className="h-3 w-3" aria-hidden />
                      {col.label}
                    </span>
                  </th>,
                  <th
                    key={`${col.key}-avg`}
                    scope="col"
                    className={classNames(
                      "px-3 py-2 text-right font-semibold",
                      col.header
                    )}
                    title={`Average ${col.label.toLowerCase()} per day`}
                  >
                    Daily avg
                  </th>,
                ];
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length > 0 ? (
              rows.map((row) => {
                const useDirectory = resolvesFromDirectory(row);
                return (
                  <tr key={row.userId} className="hover:bg-slate-50/80">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {useDirectory ? (
                          <Avatar
                            userId={row.userId}
                            classes="w-8 h-8 rounded-full"
                            textClasses="text-xs"
                          />
                        ) : (
                          <Tooltip content={row.userFullName}>
                            <span
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                              style={{
                                background: stringToColour(row.userFullName),
                              }}
                            >
                              {initialsFor(row.userFullName)}
                            </span>
                          </Tooltip>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">
                            {row.userFullName || "Unknown user"}
                          </p>
                          <p className="truncate text-[10px] text-slate-400">
                            {(
                              row.dispatched +
                              row.picked +
                              row.packed
                            ).toLocaleString("en-GB")}{" "}
                            actions total
                          </p>
                        </div>
                      </div>
                    </td>
                    {COLUMNS.map((col) => {
                      const count = countFor(row, col.key);
                      const avg = avgFor(row, col.key);
                      const isLeader =
                        count > 0 && count === leaders[col.key];
                      return [
                        <td
                          key={`${row.userId}-${col.key}-count`}
                          className={classNames(
                            "px-3 py-2 text-right font-semibold tabular-nums",
                            col.valueCell,
                            isLeader ? "ring-1 ring-inset ring-primary-300" : ""
                          )}
                        >
                          <span className="inline-flex items-center gap-1">
                            {isLeader && (
                              <span
                                className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-400"
                                aria-hidden
                                title={`Top ${col.label.toLowerCase()} this window`}
                              />
                            )}
                            {formatCount(count)}
                          </span>
                        </td>,
                        <td
                          key={`${row.userId}-${col.key}-avg`}
                          className={classNames(
                            "px-3 py-2 text-right tabular-nums",
                            col.avgCell
                          )}
                        >
                          {formatAvg(avg)}
                        </td>,
                      ];
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={1 + COLUMNS.length * 2}
                  className="px-3 py-6 text-center text-xs italic text-slate-400"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
